import { useEffect, useState } from 'react';

import {
  closestCenter,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  MeasuringStrategy,
  MouseSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type SortableItemsConfig<T> = {
  items: T[];
  onDragEnd?: (newOrder: string[]) => void;
  getId?: (item: T) => UniqueIdentifier;
};

export const useSortableDefaults = <T,>(config: SortableItemsConfig<T>) => {
  const base = useSortingDefaults();
  const { items: propItems, onDragEnd, getId = (item: any) => item.id } = config;
  const [localItems, setLocalItems] = useState(propItems);

  useEffect(() => {
    setLocalItems(propItems);
  }, [propItems]);

  const itemIds = localItems.map(getId);
  const activeItem = localItems.find((item) => getId(item) === base.activeId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeIndex = itemIds.findIndex((id) => id === active.id);
    const overIndex = itemIds.findIndex((id) => id === over?.id);
    if (activeIndex > -1 && overIndex > -1) {
      const reordered = arrayMove(localItems, activeIndex, overIndex);
      setLocalItems(reordered);
      onDragEnd?.(reordered.map((item) => String(getId(item))));
    }
    base.resetState();
  };

  return { ...base, items: localItems, itemIds, activeItem, handleDragEnd };
};

export const useSortingDefaults = () => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offset, setOffset] = useState(0);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5,
    },
  });
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
      tolerance: 10,
    },
  });
  const sensors = useSensors(pointerSensor, mouseSensor);

  const resetState = () => {
    setOverId(null);
    setActiveId(null);
    setOffset(0);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
    setOverId(event.active.id);
  };

  const handleDragMove = (event: DragMoveEvent) => setOffset(Math.round(event.delta.x / 20));
  const handleDragOver = (event: DragOverEvent) => setOverId(event.over?.id ?? null);
  const handleDragCancel = () => resetState();

  return {
    sensors,
    resetState,
    activeId,
    overId,
    offset,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragCancel,
    measuring,
    collisionDetection,
    strategy,
    setOverId,
  };
};

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const collisionDetection = closestCenter;

const strategy = verticalListSortingStrategy;

export const OverlayContainer = styled(motion.div).attrs({
  transition: { duration: 0.2 },
  initial: { scale: 1 },
  animate: { scale: 0.9 },
})({
  background: theme.color.backgroundLightDark,
  boxShadow: theme.shadow.fancy,
  borderRadius: pxToRem(4),
  marginLeft: 'auto',
});

export const BlueLine = styled.div<{ $offset?: number }>(({ $offset = 0 }) => ({
  position: 'absolute',
  minHeight: pxToRem(4),
  width: `calc(100% - (${$offset} * ${pxToRem(20)}))`,
  top: '50%',
  borderRadius: pxToRem(8),
  background: theme.color.elementBlue100,
  left: `calc(${$offset} * ${pxToRem(20)})`,
}));

export const DragHandle = styled.div<{ $isDraggable: boolean }>(({ $isDraggable }) => ({
  cursor: $isDraggable ? 'grab' : 'default',
  '&:active': {
    cursor: $isDraggable ? 'grabbing' : 'default',
  },
}));
